import { createStore as _createStore } from "zustand";
import { useUploadMultiPartFile } from "hooks/useUploadMultipartFile/useUploadMultiPartFile";
import { JsonValue } from "type-fest";
import { uuid } from "utils/uuid";
import { captureException } from "@sentry/react";
import { DataTable, Project, Tool } from "graphql/_Types";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { getToolVersionToolEditor } from "django/getToolVersion";

import { updateDraftSpec } from "../../api/updateDraftSpec";
import assert from "assert";
import { debounce, isEqual } from "lodash";
import { createTestTable as _createTestTable } from "../../api/createTestTable";
import {
  DraftSpec,
  DraftSpecParam,
} from "components/ToolEditor/ToolEditor.types";
import { ToolBooleanParamTypeV3 } from "types/json-schemas/ToolBooleanParamTypeV3";
import { ToolPathSingleParamTypeV3 } from "types/json-schemas/ToolPathSingleParamTypeV3";
import { ToolPathMultipleParamTypeV3 } from "types/json-schemas/ToolPathMultipleParamTypeV3";
import { ToolStringParamTypeV3 } from "types/json-schemas/ToolStringParamTypeV3";
import { ToolIntParamTypeV3 } from "types/json-schemas/ToolIntParamTypeV3";
import { ToolFloatParamTypeV3 } from "types/json-schemas/ToolFloatParamTypeV3";
import { isToolParamV3 } from "types/json-schemas/type_guards";
import { publishToolVersion } from "components/ToolEditor/api/publishToolVersion";
import { ToolChoiceParamTypeV3 } from "types/json-schemas/ToolChoiceParamTypeV3";
import { RESOURCE_TIERS_BY_ID, ResourceTier } from "types/ResourceTiers";
import { isNonNullish } from "utils/isNonNullish";

export const TOOL_EDITOR_PAGES = [
  { key: "CODE", title: "Upload" } as const,
  { key: "SPEC", title: "Configure" } as const,
  { key: "TEST", title: "Test" } as const,
  { key: "PUBLISH", title: "Publish" } as const,
];

export type ToolEditorPageKey = (typeof TOOL_EDITOR_PAGES)[number]["key"];

type ToolEditorQueryResult = Awaited<
  ReturnType<typeof getToolVersionToolEditor>
>;

type VersionOptions = "first_version" | "major" | "minor" | "patch";

export type ToolEditorStoreState = {
  formError: Error | null;
  project: Pick<Project, "id" | "key">;
  testTable: Pick<DataTable, "id" | "key"> | null;
  toolVersionId: number;
  tool: Pick<Tool, "id" | "key">;
  draftSpecSaveStatus: "SAVED" | "SAVING" | "UNSAVED" | "ERROR";
  isDraftSpecSynced: boolean | null;
  isCreatingTestTable: boolean;
  publishOptions: ToolEditorQueryResult["publishOptions"];
  isPublishActionEnabled: boolean;
  setIsPublishActionEnabled: (isDisabled: boolean) => void;
  /**
   * If a container image is deleted or user has lost access
   * after the initial bundle upload, we want to warn and direct users to upload
   * a new bundle
   */
  isContainerImageWarning: boolean;
  routeToPage: (key: ToolEditorPageKey) => void;
  publishVersion: (
    version: VersionOptions,
    changelog: string | undefined,
  ) => Promise<void>;
  createTestTable: () => Promise<boolean>;
  overwriteParam: (paramKey: string, newParam: DraftSpecParam) => void;
  changeSpecParamType: (
    paramKey: string,
    newParamType: DraftSpecParam["type"]["param_type"],
  ) => void;
  updateSpecParam: <T extends DraftSpecParam["type"]["param_type"]>(
    paramKey: string,
    paramType: T,
    newAttrs: Omit<
      Partial<Extract<DraftSpecParam["type"], { param_type: T }>>,
      "param_type"
    >,
    partial?: boolean,
  ) => void;
  updateSpecParamOrder: (paramKey: string, direction: "UP" | "DOWN") => void;
  updateSpecParamBaseInfo: (
    paramKey: string,
    attrs: Partial<Pick<DraftSpecParam, "description" | "name" | "required">>,
  ) => void;
  updateResult: <T extends keyof DraftSpec["results"][number]>(
    resultKey: string,
    propertyKey: T,
    value: DraftSpec["results"][number][T],
  ) => void;
  updateResourceTier: (tier: ResourceTier["id"]) => void;
  addResultColumn: () => void;
  deleteResultColumn: (resultKey: string) => void;
  updateResultOrder: (resultKey: string, direction: "UP" | "DOWN") => void;
  onClickTimelinePage: (page: ToolEditorPageKey) => void;
  uploadFile: (
    file: File,
    bundleMetadata: JsonValue,
    checksum: string,
  ) => Promise<void>;
  resetDataTableProviderKey: () => void;
  draftSpec: DraftSpec | null;
  changelogTemplate: string | null;
  setChangelogTemplate: (template: string | null) => void;
} & (
  | {
      codeUploadStatus: "UPLOADING";
      codeUploadProgress: number;
    }
  | {
      codeUploadStatus: "FAILED" | null;
      codeUploadProgress: null;
    }
);

export type CreateToolEditorStoreParams = {
  toolVersionId: number;
  uploadFile: (
    file: File,
    bundleMetadata: JsonValue,
    onUploadProgress: Parameters<
      ReturnType<typeof useUploadMultiPartFile>["uploadMultiPartFile"]
    >[0]["onUploadProgress"],
    checksum: string,
  ) => Promise<void>;
  resetDataTableProviderKey: () => void;
  setTableKey: (key: string | undefined) => void;
  routeToPage: (key: ToolEditorPageKey) => void;
};

export const createToolEditorStore = async ({
  toolVersionId,
  uploadFile: _uploadFile,
  resetDataTableProviderKey,
  setTableKey,
  routeToPage,
}: CreateToolEditorStoreParams) => {
  const toolVersion = await getToolVersionToolEditor(toolVersionId);
  setTableKey(toolVersion.test_table?.key);

  const store = _createStore<ToolEditorStoreState>((set, get, api) => {
    /**
     * Debounced function to save draft spec
     * and manage sync status store store
     */
    const saveDraftSpec = debounce(
      /**
       * Saves a draft spec and manages sync status store store
       * @param draftSpec the draft spec to save
       */
      async (draftSpec: NonNullable<ToolEditorStoreState["draftSpec"]>) => {
        try {
          set((prev) => ({ ...prev, draftSpecSaveStatus: "SAVING" }));
          const res = await updateDraftSpec(get().toolVersionId, draftSpec);
          set((prev) => ({
            ...prev,
            draftSpecSaveStatus: "SAVED",
            isDraftSpecSynced: res.is_draft_spec_synced,
          }));
        } catch (err) {
          addUtilityToastFailure("Failed to save parameters and results.");
          /**
           * TODO: Have some sort of intercept that warns users we couldn't save their data
           * and give them some steps to resolve it (i.e. back anything up they want and refresh)
           */
          set((prev) => ({ ...prev, draftSpecSaveStatus: "ERROR" }));
        }
      },
      2000,
      { leading: false, trailing: true },
    );

    /**
     * Subscribe store to changes in draft spec
     * to auto save changes
     */

    api.subscribe((state, prevState) => {
      const draftSpec = state.draftSpec;
      const prevDraftSpec = prevState.draftSpec;
      // this check prevents unnecessary save the first time a draft spec is
      // populated as non null when code upload completes
      if (prevState.codeUploadProgress !== null) {
        return;
      }

      if (draftSpec !== null && !isEqual(draftSpec, prevDraftSpec)) {
        set((prev) => ({ ...prev, draftSpecSaveStatus: "UNSAVED" }));
        void saveDraftSpec(draftSpec);
      }
    });

    const onUploadProgress = (progress: number) => {
      set((prev) => ({
        ...prev,
        codeUploadStatus: "UPLOADING",
        codeUploadProgress: progress,
      }));
    };

    const onUploadComplete = async () => {
      try {
        const toolVersion = await getToolVersionToolEditor(toolVersionId);
        const newDraftSpec = toolVersion.draft_spec;

        if (newDraftSpec === null) {
          /**
           * Unlikely but this indicates the backend would have failed to populate the draft spec
           */
          captureException("Upload completed but draft spec still null", {
            extra: toolVersion,
          });

          return set((prev) => ({
            ...prev,
            /**
             * Setting a terminal form error here
             */
            formError: new Error(
              "Something went wrong. Please try refreshing the page.",
            ),
          }));
        }

        const formPage = "SPEC" as const;

        const testTable = toolVersion.test_table;

        set((prev) => ({
          ...prev,
          codeUploadStatus: null,
          codeUploadProgress: null,
          draftSpec: toolVersion.draft_spec,
          testTable,
        }));

        routeToPage(formPage);
      } catch (err) {
        set((prev) => ({
          ...prev,
          /**
           * Setting a terminal form error because we now know an upload has completed but
           * we don't have the latest spec so we are totally out of sync with no way to recover
           */
          formError: new Error(
            "Your upload completed successfully, but we failed to retrieve your updated parameters. Please try refreshing the page.",
          ),
        }));
      }
    };

    const initialTestTable = toolVersion.test_table;
    return {
      toolVersionId,
      codeUploadStatus: null,
      codeUploadProgress: null,
      formError: null,
      isCreatingTestTable: false,
      testTable: initialTestTable,
      tool: toolVersion.tool,
      publishOptions: toolVersion.publishOptions,
      isContainerImageWarning:
        toolVersion.hasCode && !toolVersion.hasContainerImage,
      project: {
        id: toolVersion.tool.project.id,
        key: toolVersion.tool.project.key,
      },
      draftSpec: toolVersion?.draft_spec,
      isDraftSpecSynced: toolVersion.isDraftSpecSynced,
      draftSpecSaveStatus: "SAVED",
      // first version can be published without a compatibility check
      // all other versions will require a successful check before publishing is enabled
      isPublishActionEnabled: "first_version" in toolVersion.publishOptions,
      setIsPublishActionEnabled: (isPublishActionEnabled: boolean) =>
        set((prev) => ({ ...prev, isPublishActionEnabled })),
      routeToPage,
      publishVersion: async (version, changelog) => {
        const publishOptions = get().publishOptions;
        // if changelog template has a non-null value, join the user-provided changelog with the template
        // otherwise just use the user-provided changelog as-is
        const template = get().changelogTemplate;
        const combinedChangelog = (() => {
          let combinedChangelog = "";
          if (isNonNullish(changelog) && changelog.trim() !== "") {
            combinedChangelog += changelog.trim();
          }
          if (isNonNullish(template) && template.trim() !== "") {
            if (combinedChangelog !== "") {
              combinedChangelog += "\n\n";
            }
            combinedChangelog += template.trim();
          }
          return combinedChangelog !== "" ? combinedChangelog : undefined;
        })();

        if (version in publishOptions) {
          await publishToolVersion(
            get().toolVersionId,
            // checked above
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            publishOptions[version],
            combinedChangelog,
          );
        } else {
          captureException("Publish attempted with invalid version selection", {
            extra: { version, publishOptions },
          });
          throw new Error("Invalid version selection");
        }
      },
      createTestTable: async () => {
        try {
          set((prev) => ({ ...prev, isCreatingTestTable: true }));
          await _createTestTable(get().toolVersionId);
          const toolVersion = await getToolVersionToolEditor(
            get().toolVersionId,
          );
          const testTable = toolVersion.test_table;
          const formPage =
            testTable !== null ? ("TEST" as const) : ("SPEC" as const);
          set((prev) => ({
            ...prev,
            testTable: testTable,
            draftSpec: toolVersion.draft_spec,
            isDraftSpecSynced: toolVersion.isDraftSpecSynced,
            isCreatingTestTable: false,
          }));
          routeToPage(formPage);
          return true;
        } catch (err) {
          addUtilityToastFailure("Failed to create test table.");
          set((prev) => ({ ...prev, isCreatingTestTable: false }));
          return false;
        }
      },
      overwriteParam: (paramKey, newParam) => {
        const draftSpec = get().draftSpec;
        const paramIdx = get().draftSpec?.params.findIndex(
          (param) => param.key === paramKey,
        );

        if (paramIdx === undefined || paramIdx < 0 || draftSpec === null) {
          throw new Error("expected paramKey to exist in draftSpec");
        }
        const newParams = [...draftSpec.params];

        newParams.splice(paramIdx, 1, newParam);
        set((prev) => ({
          ...prev,
          draftSpec: { ...draftSpec, params: newParams },
        }));
      },
      changeSpecParamType: (
        paramKey: string,
        newParamType: DraftSpecParam["type"]["param_type"],
      ) => {
        const draftSpec = get().draftSpec;
        const paramIdx = get().draftSpec?.params.findIndex(
          (param) => param.key === paramKey,
        );

        if (paramIdx === undefined || paramIdx < 0 || draftSpec === null) {
          throw new Error("expected paramKey to exist in draftSpec");
        }

        const oldParam = draftSpec["params"][paramIdx];

        const paramType = (() => {
          switch (newParamType) {
            case "ToolBooleanParam": {
              return {
                param_type: "ToolBooleanParam",
              } satisfies ToolBooleanParamTypeV3;
            }
            case "ToolPathParam": {
              return {
                param_type: "ToolPathParam",
                multiple: false,
              } satisfies
                | ToolPathSingleParamTypeV3
                | ToolPathMultipleParamTypeV3;
            }
            case "ToolStringParam": {
              return {
                param_type: "ToolStringParam",
              } satisfies ToolStringParamTypeV3;
            }
            case "ToolIntRangeParam": {
              return {
                param_type: "ToolIntRangeParam",
              } satisfies ToolIntParamTypeV3;
            }
            case "ToolFloatRangeParam": {
              return {
                param_type: "ToolFloatRangeParam",
              } satisfies ToolFloatParamTypeV3;
            }
            case "ToolChoiceParam": {
              const choicesAndOptions = [""];
              return {
                param_type: "ToolChoiceParam",
                display_options: choicesAndOptions,
                choices: choicesAndOptions,
              } satisfies ToolChoiceParamTypeV3;
            }
            case "ToolRoiFrameParam": {
              throw new Error(
                'Not implemented yet: "ToolCellStatusParam" case',
              );
            }
            case "ToolCellStatusParam": {
              throw new Error(
                'Not implemented yet: "ToolCellStatusParam" case',
              );
            }
          }
        })();
        const newParam: DraftSpecParam = {
          name: oldParam.name,
          description: oldParam.description,
          key: oldParam.key,
          required: true,
          group: oldParam.group,
          type: { ...paramType },
        };

        const newParams = [...draftSpec.params];
        newParams.splice(paramIdx, 1, newParam);

        set((prev) => ({
          ...prev,
          draftSpec: { ...draftSpec, params: newParams },
        }));
      },
      updateSpecParamOrder: (paramKey: string, direction: "UP" | "DOWN") => {
        const draftSpec = get().draftSpec;
        if (draftSpec !== null) {
          const paramIdx = draftSpec.params.findIndex(
            (param) => param.key === paramKey,
          );
          if (paramIdx > -1) {
            switch (direction) {
              case "UP": {
                if (paramIdx === 0) {
                  return;
                }
                const newParams = [...draftSpec.params];
                const paramToMove = newParams.splice(paramIdx, 1)[0];
                newParams.splice(paramIdx - 1, 0, paramToMove);
                set((prev) => ({
                  ...prev,
                  draftSpec: { ...draftSpec, params: newParams },
                }));
                break;
              }
              case "DOWN": {
                if (paramIdx === draftSpec.params.length - 1) {
                  return;
                }
                const newParams = [...draftSpec.params];
                const paramToMove = newParams.splice(paramIdx, 1)[0];
                newParams.splice(paramIdx + 1, 0, paramToMove);
                set((prev) => ({
                  ...prev,
                  draftSpec: { ...draftSpec, params: newParams },
                }));
                break;
              }
            }
          }
        }
      },
      updateResult: (resultKey, propertyKey, value) => {
        const draftSpec = get().draftSpec;
        assert(draftSpec !== null, "Expected draftSpec in updateResult");

        const newDraftSpec = { ...draftSpec };
        const newResults = [...newDraftSpec.results];

        const resultIdx = newResults.findIndex(
          (result) => result.key === resultKey,
        );
        newResults[resultIdx] = {
          ...newResults[resultIdx],
          [propertyKey]: value,
        };
        newDraftSpec.results = newResults;
        set((prev) => ({ ...prev, draftSpec: newDraftSpec }));
      },
      addResultColumn: () => {
        const draftSpec = get().draftSpec;
        assert(draftSpec !== null, "Expected draftSpec in updateResult");

        const newDraftSpec = { ...draftSpec };

        const newResults = [...newDraftSpec.results];
        newResults.push({
          key: uuid(),
          name: "Result Column",
          description: "Result Column",
          file_patterns: ["**/*"],
        });

        newDraftSpec.results = newResults;
        set((prev) => ({ ...prev, draftSpec: newDraftSpec }));
      },
      deleteResultColumn: (resultKey) => {
        const draftSpec = get().draftSpec;
        assert(draftSpec !== null, "Expected draftSpec in updateResult");

        const newDraftSpec = { ...draftSpec };

        const newResults = [...newDraftSpec.results];

        const colIdx = newResults.findIndex(
          (result) => result.key === resultKey,
        );
        assert(colIdx > -1, "Result not found when deleting col");

        newResults.splice(colIdx, 1);

        newDraftSpec.results = newResults;
        set((prev) => ({ ...prev, draftSpec: newDraftSpec }));
      },
      updateResultOrder: (resultKey: string, direction: "UP" | "DOWN") => {
        const draftSpec = get().draftSpec;
        if (draftSpec !== null) {
          const resultIdx = draftSpec.results.findIndex(
            (param) => param.key === resultKey,
          );
          if (resultIdx > -1) {
            switch (direction) {
              case "UP": {
                if (resultIdx === 0) {
                  return;
                }
                const newResults = [...draftSpec.results];
                const resultToMove = newResults.splice(resultIdx, 1)[0];
                newResults.splice(resultIdx - 1, 0, resultToMove);
                set((prev) => ({
                  ...prev,
                  draftSpec: { ...draftSpec, results: newResults },
                }));
                break;
              }
              case "DOWN": {
                if (resultIdx === draftSpec.params.length - 1) {
                  return;
                }
                const newResults = [...draftSpec.results];
                const resultToMove = newResults.splice(resultIdx, 1)[0];
                newResults.splice(resultIdx + 1, 0, resultToMove);
                set((prev) => ({
                  ...prev,
                  draftSpec: { ...draftSpec, results: newResults },
                }));
                break;
              }
            }
          }
        }
      },
      updateSpecParam: (paramKey, paramType, newAttrs, partial = true) => {
        const draftSpec = get().draftSpec;
        const paramIdx = get().draftSpec?.params?.findIndex(
          (param) => param.key === paramKey,
        );

        if (paramIdx === undefined || paramIdx < 0 || draftSpec === null) {
          throw new Error("expected paramKey to exist in draftSpec");
        }

        const oldParam = draftSpec.params[paramIdx];
        assert(isToolParamV3(oldParam, paramType), `Expected ${paramType}`);

        const typeAttrs = partial
          ? { ...oldParam.type, ...newAttrs }
          : {
              param_type: oldParam.type.param_type,
              ...newAttrs,
            };
        const newParams = [...draftSpec.params];

        newParams[paramIdx] = { ...newParams[paramIdx] };

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        newParams[paramIdx].type = typeAttrs;

        set((prev) => ({
          ...prev,
          draftSpec: { ...draftSpec, params: newParams },
        }));
      },
      updateSpecParamBaseInfo: (paramKey, attrs) => {
        const draftSpec = get().draftSpec;
        const paramIdx = get().draftSpec?.params?.findIndex(
          (param) => param.key === paramKey,
        );

        if (paramIdx === undefined || paramIdx < 0 || draftSpec === null) {
          throw new Error("expected paramKey to exist in draftSpec");
        }

        const newParam = { ...draftSpec.params[paramIdx], ...attrs };

        const newParams = [...draftSpec.params];

        newParams.splice(paramIdx, 1, newParam);

        set((prev) => ({
          ...prev,
          draftSpec: { ...draftSpec, params: newParams },
        }));
      },
      updateResourceTier: (tier) => {
        const resourceTier = RESOURCE_TIERS_BY_ID[tier];
        const draftSpec = get().draftSpec;
        if (draftSpec === null) {
          return captureException(
            "Attempted to update resource tier with no draft spec",
          );
        }
        set((prev) => ({
          ...prev,
          draftSpec: {
            ...draftSpec,
            resources: {
              cpu: resourceTier.cpu,
              gpu: resourceTier.gpu,
              memory: resourceTier.memory,
            },
          },
        }));
      },
      onClickTimelinePage: (formPage) => {
        const hasTestTable = get().testTable !== null;
        if (formPage === "TEST" && !hasTestTable) {
          void get()
            .createTestTable()
            .then((isSuccessful) => {
              if (isSuccessful) {
                resetDataTableProviderKey();
                routeToPage(formPage);
              }
            });
        } else {
          routeToPage(formPage);
        }
      },
      uploadFile: async (
        file: File,
        bundleMetadata: JsonValue,
        checksum: string,
      ) => {
        set((prev) => ({
          ...prev,
          codeUploadStatus: "UPLOADING",
          codeUploadProgress: 0,
        }));

        try {
          await _uploadFile(file, bundleMetadata, onUploadProgress, checksum);
          addUtilityToastSuccess("Successfully uploaded code bundle.");
          await onUploadComplete();
        } catch (err) {
          /**
           * Upload has failed. This is still a recoverable state since nothing happened
           * to code or draft_spec at this point. We can display a notice and user can just try again.
           */
          set({ codeUploadStatus: "FAILED", codeUploadProgress: null });
          addUtilityToastFailure("Failed to upload code bundle.");
        }
      },
      resetDataTableProviderKey: () => {
        resetDataTableProviderKey();
      },
      changelogTemplate: null,
      setChangelogTemplate: (template: string | null) => {
        set((prev) => ({
          ...prev,
          changelogTemplate: template,
        }));
      },
    };
  });
  return store;
};
